import { useEffect, useMemo, useState } from 'react';
import { ANALYTICS_SCREEN_IDS } from './analyticsScreenDefaults';

export const ANALYTICS_FILTER_KEYS = {
  PERIOD: 'period',
  DEPARTMENTS: 'departments',
  ROLES: 'roles',
  RECRUITERS: 'recruiters',
  SOURCES: 'sources',
  TAGS: 'tags',
  LOCATIONS: 'locations'
};

export const ANALYTICS_GROUP_BY_KEYS = {
  DEPARTMENT: 'department',
  ROLE: 'role',
  RECRUITER: 'recruiter',
  SOURCE: 'source',
  TAG: 'tag',
  LOCATION: 'location'
};

export const ANALYTICS_GROUP_BY_OPTIONS = [
  ANALYTICS_GROUP_BY_KEYS.DEPARTMENT,
  ANALYTICS_GROUP_BY_KEYS.ROLE,
  ANALYTICS_GROUP_BY_KEYS.RECRUITER,
  ANALYTICS_GROUP_BY_KEYS.SOURCE,
  ANALYTICS_GROUP_BY_KEYS.TAG,
  ANALYTICS_GROUP_BY_KEYS.LOCATION
];

export const ANALYTICS_FILTER_KEY_BY_GROUP_BY = {
  [ANALYTICS_GROUP_BY_KEYS.DEPARTMENT]: ANALYTICS_FILTER_KEYS.DEPARTMENTS,
  [ANALYTICS_GROUP_BY_KEYS.ROLE]: ANALYTICS_FILTER_KEYS.ROLES,
  [ANALYTICS_GROUP_BY_KEYS.RECRUITER]: ANALYTICS_FILTER_KEYS.RECRUITERS,
  [ANALYTICS_GROUP_BY_KEYS.SOURCE]: ANALYTICS_FILTER_KEYS.SOURCES,
  [ANALYTICS_GROUP_BY_KEYS.TAG]: ANALYTICS_FILTER_KEYS.TAGS,
  [ANALYTICS_GROUP_BY_KEYS.LOCATION]: ANALYTICS_FILTER_KEYS.LOCATIONS
};

const STORAGE_PREFIX = 'analytics-screen-filters-v1';

const FILTER_LABELS = {
  en: {
    period: 'Period',
    departments: 'Departments',
    roles: 'Roles',
    recruiters: 'Recruiters',
    sources: 'Sources',
    tags: 'Tags',
    locations: 'Locations',
    groupBy: 'Group by',
    allDepartments: 'All departments',
    allRoles: 'All roles',
    allRecruiters: 'All recruiters',
    allSources: 'All sources',
    allTags: 'All tags',
    allLocations: 'All locations',
    department: 'Department',
    recruiter: 'Recruiter',
    source: 'Source',
    role: 'Role',
    tag: 'Tag',
    location: 'Location'
  },
  ar: {
    period: 'الفترة',
    departments: 'الأقسام',
    roles: 'الوظائف',
    recruiters: 'مسؤولو التوظيف',
    sources: 'المصادر',
    tags: 'الوسوم',
    locations: 'المواقع',
    groupBy: 'التجميع حسب',
    allDepartments: 'جميع الأقسام',
    allRoles: 'جميع الوظائف',
    allRecruiters: 'جميع مسؤولي التوظيف',
    allSources: 'جميع المصادر',
    allTags: 'جميع الوسوم',
    allLocations: 'جميع المواقع',
    department: 'القسم',
    recruiter: 'مسؤول التوظيف',
    source: 'المصدر',
    role: 'الوظيفة',
    tag: 'الوسم',
    location: 'الموقع'
  }
};

const getSelectedCountLabel = (count, isRTL = false) => {
  if (isRTL) {
    return count === 1 ? 'عنصر واحد' : `${count} عناصر`;
  }
  return count === 1 ? '1 selected' : `${count} selected`;
};

const PERIOD_PRESETS = {
  [ANALYTICS_SCREEN_IDS.EXECUTIVE_OVERVIEW]: [
    { id: 'last_30_days', label: { en: 'Last 30 days', ar: 'آخر 30 يومًا' } },
    { id: 'last_60_days', label: { en: 'Last 60 days', ar: 'آخر 60 يومًا' } },
    { id: 'last_90_days', label: { en: 'Last 90 days', ar: 'آخر 90 يومًا' } },
    { id: 'last_12_months', label: { en: 'Last 12 months', ar: 'آخر 12 شهرًا' } },
    { id: 'last_24_months', label: { en: 'Last 24 months', ar: 'آخر 24 شهرًا' } }
  ],
  [ANALYTICS_SCREEN_IDS.RECRUITMENT]: [
    { id: 'last_30_days', label: { en: 'Last 30 days', ar: 'آخر 30 يومًا' } },
    { id: 'last_60_days', label: { en: 'Last 60 days', ar: 'آخر 60 يومًا' } },
    { id: 'last_90_days', label: { en: 'Last 90 days', ar: 'آخر 90 يومًا' } },
    { id: 'last_12_months', label: { en: 'Last 12 months', ar: 'آخر 12 شهرًا' } },
    { id: 'last_24_months', label: { en: 'Last 24 months', ar: 'آخر 24 شهرًا' } }
  ],
  [ANALYTICS_SCREEN_IDS.PIPELINE_RISK]: [
    { id: 'last_30_days', label: { en: 'Last 30 days', ar: 'آخر 30 يومًا' } },
    { id: 'last_60_days', label: { en: 'Last 60 days', ar: 'آخر 60 يومًا' } },
    { id: 'quarter_to_date', label: { en: 'Quarter to date', ar: 'الربع حتى الآن' } },
    { id: 'last_12_months', label: { en: 'Last 12 months', ar: 'آخر 12 شهرًا' } },
    { id: 'last_24_months', label: { en: 'Last 24 months', ar: 'آخر 24 شهرًا' } }
  ],
  [ANALYTICS_SCREEN_IDS.QUALITY_OF_HIRES]: [
    { id: 'last_90_days', label: { en: 'Last 90 days', ar: 'آخر 90 يومًا' } },
    { id: 'last_12_months', label: { en: 'Last 12 months', ar: 'آخر 12 شهرًا' } },
    { id: 'last_24_months', label: { en: 'Last 24 months', ar: 'آخر 24 شهرًا' } },
    { id: 'year_to_date', label: { en: 'Year to date', ar: 'منذ بداية السنة' } }
  ],
  [ANALYTICS_SCREEN_IDS.RECRUITER_PERFORMANCE]: [
    { id: 'last_30_days', label: { en: 'Last 30 days', ar: 'آخر 30 يومًا' } },
    { id: 'last_90_days', label: { en: 'Last 90 days', ar: 'آخر 90 يومًا' } },
    { id: 'last_12_months', label: { en: 'Last 12 months', ar: 'آخر 12 شهرًا' } },
    { id: 'last_24_months', label: { en: 'Last 24 months', ar: 'آخر 24 شهرًا' } },
    { id: 'quarter_to_date', label: { en: 'Quarter to date', ar: 'الربع حتى الآن' } }
  ]
};

export const ANALYTICS_SCREEN_FILTERS = {
  [ANALYTICS_SCREEN_IDS.EXECUTIVE_OVERVIEW]: [
    ANALYTICS_FILTER_KEYS.PERIOD
  ],
  [ANALYTICS_SCREEN_IDS.RECRUITMENT]: [
    ANALYTICS_FILTER_KEYS.PERIOD
  ],
  [ANALYTICS_SCREEN_IDS.PIPELINE_RISK]: [
  ],
  [ANALYTICS_SCREEN_IDS.QUALITY_OF_HIRES]: [
    ANALYTICS_FILTER_KEYS.PERIOD
  ],
  [ANALYTICS_SCREEN_IDS.RECRUITER_PERFORMANCE]: [
    ANALYTICS_FILTER_KEYS.PERIOD
  ]
};

export const ANALYTICS_SCREEN_MULTISELECT_FILTERS = {
  [ANALYTICS_SCREEN_IDS.RECRUITER_PERFORMANCE]: []
};

const DEFAULT_PERIOD_BY_SCREEN = {
  [ANALYTICS_SCREEN_IDS.EXECUTIVE_OVERVIEW]: 'last_90_days',
  [ANALYTICS_SCREEN_IDS.RECRUITMENT]: 'last_60_days',
  [ANALYTICS_SCREEN_IDS.PIPELINE_RISK]: 'last_60_days',
  [ANALYTICS_SCREEN_IDS.QUALITY_OF_HIRES]: 'last_12_months',
  [ANALYTICS_SCREEN_IDS.RECRUITER_PERFORMANCE]: 'last_90_days'
};

const dedupeOptions = (options = []) => {
  const seen = new Set();
  return options.reduce((accumulator, option) => {
    if (!option || !option.id || seen.has(option.id)) {
      return accumulator;
    }
    seen.add(option.id);
    accumulator.push({
      id: String(option.id),
      label: String(option.label || option.id)
    });
    return accumulator;
  }, []);
};

const getAllLabel = (filterId, isRTL) => {
  const labels = isRTL ? FILTER_LABELS.ar : FILTER_LABELS.en;
  const allLabels = {
    [ANALYTICS_FILTER_KEYS.DEPARTMENTS]: labels.allDepartments,
    [ANALYTICS_FILTER_KEYS.ROLES]: labels.allRoles,
    [ANALYTICS_FILTER_KEYS.RECRUITERS]: labels.allRecruiters,
    [ANALYTICS_FILTER_KEYS.SOURCES]: labels.allSources,
    [ANALYTICS_FILTER_KEYS.TAGS]: labels.allTags,
    [ANALYTICS_FILTER_KEYS.LOCATIONS]: labels.allLocations
  };

  return allLabels[filterId] || labels.period;
};

export const createDefaultScreenFilterState = (screenId) =>
  (ANALYTICS_SCREEN_FILTERS[screenId] || []).reduce((accumulator, filterId) => {
    accumulator[filterId] =
      filterId === ANALYTICS_FILTER_KEYS.PERIOD ? DEFAULT_PERIOD_BY_SCREEN[screenId] || 'last_30_days' : 'all';
    return accumulator;
  }, {});

const parseStoredState = (screenId) => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(`${STORAGE_PREFIX}:${screenId}`);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const persistStoredState = (screenId, filterState) => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  try {
    window.sessionStorage.setItem(`${STORAGE_PREFIX}:${screenId}`, JSON.stringify(filterState));
  } catch (error) {
    // Ignore session storage failures and keep filters in memory only.
  }
};

export const useSessionAnalyticsFilters = ({ screenId }) => {
  const defaultState = useMemo(() => createDefaultScreenFilterState(screenId), [screenId]);
  const [filterState, setFilterState] = useState(() => ({
    ...defaultState,
    ...(parseStoredState(screenId) || {})
  }));

  useEffect(() => {
    setFilterState((current) => ({
      ...defaultState,
      ...current
    }));
  }, [defaultState]);

  useEffect(() => {
    persistStoredState(screenId, filterState);
  }, [screenId, filterState]);

  const setFilterValue = (filterId, value) => {
    setFilterState((current) => ({
      ...current,
      [filterId]: value
    }));
  };

  const resetFilters = () => {
    setFilterState(defaultState);
  };

  return {
    filterState,
    setFilterValue,
    resetFilters
  };
};

export const getLocalizedGroupByOptions = (isRTL = false) => {
  const labels = isRTL ? FILTER_LABELS.ar : FILTER_LABELS.en;
  return ANALYTICS_GROUP_BY_OPTIONS.map((id) => ({
    id,
    label: labels[id]
  }));
};

export const getSupportedAnalyticsFilterState = ({
  filterState = {},
  supportedFilters = [],
  groupBy = ''
}) => {
  if (!supportedFilters.length) {
    return {};
  }

  const nextState = Object.entries(filterState).reduce((accumulator, [filterId, value]) => {
    if (supportedFilters.includes(filterId)) {
      accumulator[filterId] = value;
    }
    return accumulator;
  }, {});

  const comparisonFilterKey = ANALYTICS_FILTER_KEY_BY_GROUP_BY[groupBy];
  if (comparisonFilterKey && comparisonFilterKey in nextState) {
    delete nextState[comparisonFilterKey];
  }

  return nextState;
};

export const isDefaultAnalyticsFilterState = ({
  screenId,
  filterState = {},
  supportedFilters = []
}) => {
  if (!supportedFilters.length) {
    return true;
  }

  const defaults = createDefaultScreenFilterState(screenId);

  return supportedFilters.every((filterId) => {
    const currentValue = filterState[filterId];
    const defaultValue = defaults[filterId];

    if (Array.isArray(currentValue) || Array.isArray(defaultValue)) {
      const normalizedCurrent = Array.isArray(currentValue)
        ? currentValue.filter((value) => value !== 'all')
        : currentValue === 'all' || currentValue === undefined || currentValue === null
          ? []
          : [currentValue];
      const normalizedDefault = Array.isArray(defaultValue)
        ? defaultValue.filter((value) => value !== 'all')
        : defaultValue === 'all' || defaultValue === undefined || defaultValue === null
          ? []
          : [defaultValue];
      if (normalizedCurrent.length !== normalizedDefault.length) {
        return false;
      }
      return normalizedCurrent.every((value, index) => value === normalizedDefault[index]);
    }

    return (currentValue ?? defaultValue) === defaultValue;
  });
};

export const canUseMaterializedAnalyticsBundle = ({
  screenId,
  filterState = {},
  supportedFilters = [],
  supportsGroupBy = false,
  activeGroupBy = '',
  defaultGroupBy = '',
  materializedGroupBy = []
}) => {
  const filtersAreDefault = isDefaultAnalyticsFilterState({
    screenId,
    filterState,
    supportedFilters
  });

  if (!filtersAreDefault) {
    return false;
  }

  if (!supportsGroupBy) {
    return true;
  }

  if (!activeGroupBy || activeGroupBy === defaultGroupBy) {
    return true;
  }

  return materializedGroupBy.includes(activeGroupBy);
};

export const getLocalizedFilterLabel = (filterId, isRTL = false) => {
  const labels = isRTL ? FILTER_LABELS.ar : FILTER_LABELS.en;
  return labels[filterId] || filterId;
};

export const normalizeAnalyticsScreenFilterOptions = ({ screenId, isRTL = false, remoteOptions = {}, fallbackOptions = {} }) => {
  const filterIds = ANALYTICS_SCREEN_FILTERS[screenId] || [];
  const periodOptions = (PERIOD_PRESETS[screenId] || []).map((option) => ({
    id: option.id,
    label: option.label[isRTL ? 'ar' : 'en']
  }));

  return filterIds.reduce((accumulator, filterId) => {
    if (filterId === ANALYTICS_FILTER_KEYS.PERIOD) {
      accumulator[filterId] = periodOptions;
      return accumulator;
    }

    accumulator[filterId] = dedupeOptions(remoteOptions[filterId] || fallbackOptions[filterId] || []);
    return accumulator;
  }, {});
};

export const buildScreenFilterModels = ({ screenId, isRTL = false, filterState = {}, filterOptions = {} }) => {
  const filterIds = ANALYTICS_SCREEN_FILTERS[screenId] || [];
  const multiSelectFilters = ANALYTICS_SCREEN_MULTISELECT_FILTERS[screenId] || [];

  return filterIds.map((filterId) => {
    const rawOptions = filterOptions[filterId] || [];
    if (filterId !== ANALYTICS_FILTER_KEYS.PERIOD && !rawOptions.length) {
      return null;
    }

    const options = filterId === ANALYTICS_FILTER_KEYS.PERIOD
      ? rawOptions
      : [{ id: 'all', label: getAllLabel(filterId, isRTL) }, ...rawOptions];
    const isMulti = multiSelectFilters.includes(filterId);
    const defaultValue = filterId === ANALYTICS_FILTER_KEYS.PERIOD ? DEFAULT_PERIOD_BY_SCREEN[screenId] : 'all';
    const rawValue = filterState[filterId] ?? defaultValue;

    if (isMulti) {
      const selectedIds = Array.isArray(rawValue)
        ? rawValue.filter((value) => value !== 'all')
        : rawValue === 'all' || !rawValue
          ? []
          : [rawValue];
      const selectedOptions = options.filter((option) => selectedIds.includes(option.id));
      const value =
        selectedOptions.length === 0
          ? getAllLabel(filterId, isRTL)
          : selectedOptions.length === 1
            ? selectedOptions[0].label
            : getSelectedCountLabel(selectedOptions.length, isRTL);

      return {
        id: filterId,
        label: getLocalizedFilterLabel(filterId, isRTL),
        value,
        selectedId: selectedIds[0] || 'all',
        selectedIds,
        options,
        isMulti
      };
    }

    const selectedId = rawValue;
    const selectedOption = options.find((option) => option.id === selectedId) || options[0] || { label: '' };

    return {
      id: filterId,
      label: getLocalizedFilterLabel(filterId, isRTL),
      value: selectedOption?.label || '',
      selectedId,
      selectedIds: selectedId === 'all' ? [] : [selectedId],
      options,
      isMulti: false
    };
  }).filter(Boolean);
};
